import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { apiPost } from '../../../src/api/client';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import DateTimePicker from '../../../src/components/DateTimePicker';
import { useToast } from '../../../src/components/Toast';
import ContentContainer from '../../../src/components/ContentContainer';
import { useTheme } from '../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import { titleCase, formatPhone } from '../../../src/utils/format';

interface LineItem {
  id: string;
  washType: string;
  retailPrice: number;
  createdAt: string;
  bayName: string;
  userName: string | null;
  userEmail: string | null;
  userPhone: string | null;
}

interface LocationGroup {
  locationId: string;
  locationName: string;
  taxJurisdiction: string | null;
  taxRate: number;
  taxRateState: number;
  taxRateLocal: number;
  groupSubtotal: number;
  groupDiscount: number;
  groupTax: number;
  groupTotal: number;
  lineItems: LineItem[];
}

interface InvoicePreview {
  groups: LocationGroup[];
  subtotal: number;
  discountPercentage: number | null;
  discountAmount: number;
  taxAmount: number;
  totalAmount: number;
  transactionCount: number;
  tierName: string | null;
}

/** GenerateInvoiceScreen - preview and generate fleet invoices by billing period. */
export default function GenerateInvoiceScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const { fleetId } = useLocalSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<InvoicePreview | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [billingPeriodStart, setBillingPeriodStart] = useState('');
  const [billingPeriodEnd, setBillingPeriodEnd] = useState('');
  const router = useRouter();
  const toast = useToast();

  // The pickers hold owner-local wall-clock strings ("YYYY-MM-DDTHH:mm").
  // new Date(...) reads them in the device's tz (which matches the owner's),
  // yielding the correct UTC instant the invoice API filters on.
  const getBody = () => ({
    fleetAccountId: fleetId,
    billingPeriodStart: new Date(billingPeriodStart).toISOString(),
    billingPeriodEnd: new Date(billingPeriodEnd).toISOString(),
  });

  const handlePreview = async () => {
    if (!billingPeriodStart || !billingPeriodEnd) {
      toast.show({ type: 'error', title: 'Error', message: 'Please select billing period dates' });
      return;
    }

    try {
      setPreviewing(true);
      const data = await apiPost<InvoicePreview>('/api/fleet/invoices/preview', getBody());
      setPreview(data);
      // Select all by default — collect ids across every location group
      const allIds = data.groups.flatMap(g => g.lineItems.map(i => i.id));
      setSelectedIds(new Set(allIds));
      if (data.transactionCount === 0) {
        toast.show({ type: 'info', title: 'No Transactions', message: 'No unbilled transactions in this period' });
      }
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setPreviewing(false);
    }
  };

  const toggleItem = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allIds = useMemo(() => preview ? preview.groups.flatMap(g => g.lineItems.map(i => i.id)) : [], [preview]);

  const toggleAll = () => {
    if (!preview) return;
    if (selectedIds.size === allIds.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(allIds));
  };

  // Recalculate totals locally based on selected items + each location's tax rate from preview.
  const totals = useMemo(() => {
    if (!preview) return { subtotal: 0, discountAmount: 0, taxAmount: 0, totalAmount: 0, count: 0 };
    const discountPct = preview.discountPercentage || 0;
    let subtotal = 0, discountAmount = 0, taxAmount = 0, count = 0;
    for (const g of preview.groups) {
      const groupSelected = g.lineItems.filter(i => selectedIds.has(i.id));
      if (groupSelected.length === 0) continue;
      const groupSubtotal = groupSelected.reduce((s, t) => s + t.retailPrice, 0);
      const groupDiscount = discountPct ? Math.round(groupSubtotal * discountPct / 100) : 0;
      const groupTax = Math.round((groupSubtotal - groupDiscount) * g.taxRate);
      subtotal += groupSubtotal;
      discountAmount += groupDiscount;
      taxAmount += groupTax;
      count += groupSelected.length;
    }
    return { subtotal, discountAmount, taxAmount, totalAmount: subtotal - discountAmount + taxAmount, count };
  }, [preview, selectedIds]);

  const handleGenerate = async () => {
    if (!billingPeriodStart || !billingPeriodEnd) {
      toast.show({ type: 'error', title: 'Error', message: 'Please select billing period dates' });
      return;
    }

    if (selectedIds.size === 0) {
      toast.show({ type: 'error', title: 'Error', message: 'Select at least one transaction' });
      return;
    }

    try {
      setSubmitting(true);
      await apiPost('/api/fleet/invoices/generate', {
        ...getBody(),
        transactionIds: Array.from(selectedIds),
      });
      toast.show({ type: 'success', title: 'Success', message: 'Invoice generated and ready to send' });
      router.back();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatWashType = (t: string) => {
    switch (t) {
      case 'self-serve': return 'Self-Serve';
      case 'automatic_basic': return 'Basic Wash';
      case 'automatic_premium': return 'Premium Wash';
      case 'automatic_deluxe': return 'Deluxe Wash';
      default: return t;
    }
  };

  const suggestCurrentMonth = () => {
    const now = new Date();
    const p2 = (n: number) => String(n).padStart(2, '0');
    const wall = (d: Date, h: number, mi: number) =>
      `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}T${p2(h)}:${p2(mi)}`;
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setBillingPeriodStart(wall(start, 0, 0));
    setBillingPeriodEnd(wall(end, 23, 59));
    setPreview(null);
    setSelectedIds(new Set());
  };

  const allSelected = preview ? selectedIds.size === allIds.length : false;
  const fmtPct = (rate: number) => `${(rate * 100).toFixed(3).replace(/\.?0+$/, '')}%`;

  return (
    <ScrollView style={styles.container}>
      <ContentContainer>
      {/* Quick Action */}
      <Card style={styles.quickCard}>
        <View style={styles.quickHeader}>
          <Ionicons name="calendar" size={24} color={colors.primary} />
          <Text style={styles.quickTitle}>Quick Select</Text>
        </View>
        <Button
          title="Current Month"
          onPress={suggestCurrentMonth}
          variant="outline"
          size="sm"
        />
      </Card>

      {/* Form */}
      <Card>
        <Text style={styles.sectionTitle}>Billing Period</Text>

        <View style={styles.dateField}>
          <DateTimePicker
            value={billingPeriodStart}
            onChange={(v) => { setBillingPeriodStart(v); setPreview(null); setSelectedIds(new Set()); }}
            label="Start *"
          />
        </View>
        <View style={styles.dateField}>
          <DateTimePicker
            value={billingPeriodEnd}
            onChange={(v) => { setBillingPeriodEnd(v); setPreview(null); setSelectedIds(new Set()); }}
            label="End *"
          />
        </View>

        {/* Tax rate is configured in fleet account settings */}

        <Button
          title={previewing ? 'Loading...' : 'Preview Invoice'}
          onPress={handlePreview}
          variant="outline"
          disabled={previewing}
          icon={previewing ? undefined : <Ionicons name="eye" size={20} color={colors.primary} />}
        />
      </Card>

      {/* Preview */}
      {preview && preview.transactionCount > 0 && (
        <Card style={styles.previewCard}>
          <View style={styles.previewHeader}>
            <Text style={styles.previewTitle}>
              {totals.count} of {preview.transactionCount} selected
            </Text>
            <TouchableOpacity onPress={toggleAll} style={styles.selectAllBtn}>
              <Ionicons
                name={allSelected ? 'checkbox' : 'square-outline'}
                size={20}
                color={colors.primary}
              />
              <Text style={styles.selectAllText}>{allSelected ? 'Deselect All' : 'Select All'}</Text>
            </TouchableOpacity>
          </View>

          {/* Transactions grouped by location, each with its own per-location tax rate */}
          {preview.groups.map(group => (
            <View key={group.locationId} style={{ marginBottom: spacing.lg }}>
              <View style={styles.lineItemsHeader}>
                <Text style={[styles.lineItemHeaderText, { flex: 1, fontWeight: '700' }]}>
                  {group.locationName}{group.taxJurisdiction ? ` — ${group.taxJurisdiction}` : ''} (tax {fmtPct(group.taxRate)})
                </Text>
              </View>
              {group.lineItems.map(item => {
                const checked = selectedIds.has(item.id);
                return (
                  <TouchableOpacity key={item.id} style={styles.lineItem} onPress={() => toggleItem(item.id)} activeOpacity={0.6}>
                    <View style={styles.checkboxWrap}>
                      <Ionicons
                        name={checked ? 'checkbox' : 'square-outline'}
                        size={22}
                        color={checked ? colors.primary : colors.textLight}
                      />
                    </View>
                    <View style={{ flex: 1, opacity: checked ? 1 : 0.4 }}>
                      <Text style={styles.lineItemDesc}>
                        {formatWashType(item.washType)}{item.bayName ? ` — ${item.bayName}` : ''}
                      </Text>
                      <Text style={styles.lineItemSub}>{formatDate(item.createdAt)}</Text>
                      {(item.userName || item.userEmail || item.userPhone) ? (
                        <Text style={styles.lineItemSub}>
                          {[item.userName ? titleCase(item.userName) : null, item.userEmail, item.userPhone ? formatPhone(item.userPhone) : null].filter(Boolean).join(' · ')}
                        </Text>
                      ) : null}
                    </View>
                    <Text style={[styles.lineItemAmount, !checked && { opacity: 0.4 }]}>{fmt(item.retailPrice)}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          ))}

          {/* Totals section */}
          <View style={styles.totalsSection}>
            <View style={styles.totalRow}>
              <Text style={styles.subtotalLabel}>Subtotal (all locations)</Text>
              <Text style={styles.subtotalValue}>{fmt(totals.subtotal)}</Text>
            </View>

            {totals.discountAmount > 0 && (
              <View style={styles.totalRow}>
                <Text style={[styles.totalLabel, { color: colors.success }]}>
                  Fleet Discount{preview?.discountPercentage ? ` (${preview.discountPercentage}%)` : ''}
                </Text>
                <Text style={[styles.totalValue, { color: colors.success }]}>-{fmt(totals.discountAmount)}</Text>
              </View>
            )}

            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Total Tax</Text>
              <Text style={styles.totalValue}>{fmt(totals.taxAmount)}</Text>
            </View>

            <View style={[styles.totalRow, styles.grandTotalRow]}>
              <Text style={styles.grandTotalLabel}>Total Due</Text>
              <Text style={styles.grandTotalValue}>{fmt(totals.totalAmount)}</Text>
            </View>
          </View>
        </Card>
      )}

      {preview && preview.transactionCount === 0 && (
        <Card style={styles.emptyPreview}>
          <Ionicons name="document-text-outline" size={48} color={colors.textLight} />
          <Text style={styles.emptyTitle}>No Transactions</Text>
          <Text style={styles.emptyText}>No unbilled transactions found for this billing period.</Text>
        </Card>
      )}

      {/* Actions */}
      <View style={styles.actions}>
        <Button
          title="Cancel"
          onPress={() => router.back()}
          variant="outline"
          style={{ flex: 1 }}
        />
        <Button
          title={`Generate Invoice${totals.count > 0 ? ` (${totals.count})` : ''}`}
          onPress={handleGenerate}
          loading={submitting}
          disabled={!preview || totals.count === 0}
          icon={<Ionicons name="document-text" size={20} color={colors.white} />}
          style={{ flex: 1 }}
        />
      </View>

      <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  headerTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },

  quickCard: { marginBottom: spacing.md },
  quickHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.md },
  quickTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },

  sectionTitle: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
    color: c.text,
    marginBottom: spacing.md,
  },

  dateField: { marginBottom: spacing.md },
  dateRow: { flexDirection: 'row', marginBottom: spacing.md },
  dateLabel: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    color: c.text,
    marginBottom: spacing.xs,
  },

  previewCard: { marginTop: spacing.md },
  previewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  previewTitle: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.bold,
    color: c.text,
  },
  selectAllBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  selectAllText: {
    fontSize: fontSize.sm,
    color: c.primary,
    fontWeight: fontWeight.medium,
  },

  lineItemsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: c.border,
    marginBottom: spacing.xs,
  },
  lineItemHeaderText: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
    color: c.textLight,
    textTransform: 'uppercase',
  },

  lineItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: c.borderLight,
  },
  checkboxWrap: {
    width: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  lineItemDesc: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  lineItemSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 1 },
  lineItemAmount: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text, width: 80, textAlign: 'right' },

  totalsSection: {
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 2,
    borderTopColor: c.border,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing.xs,
  },
  totalLabel: { fontSize: fontSize.sm, color: c.textSecondary },
  totalValue: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.textSecondary },

  subtotalRow: {
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: c.borderLight,
    marginTop: spacing.xs,
  },
  subtotalLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  subtotalValue: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },

  grandTotalRow: {
    paddingTop: spacing.md,
    borderTopWidth: 2,
    borderTopColor: c.text,
    marginTop: spacing.sm,
  },
  grandTotalLabel: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  grandTotalValue: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.primary },

  emptyPreview: { marginTop: spacing.md, alignItems: 'center', paddingVertical: spacing.xxl },
  emptyTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.md },
  emptyText: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', marginTop: spacing.xs },

  actions: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.lg },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
